import { useQuery } from "@tanstack/react-query";
import { Button, Dropdown, message, Modal, Space, Tag, Tooltip } from "antd";
import { useState } from "react";
import { RxDotsVertical } from "react-icons/rx";
import { Link } from "react-router-dom";
import {
  campaignAccept,
  campaignReject,
  deleteCampaign,
  getAllCampaigns,
} from "../actions/acc_actions";
import ContentLayout from "../components/layout/ContentLayout";
import SearchBar from "../components/SearchBar";
import TableMed from "../components/TableMed";
import Loading from "../utils/Loading";

const getStatusTag = (status) => {
  const colorMap = {
    COMPLETED: "green",
    PENDING: "orange",
    FAILED: "red",
    CANCELLED: "volcano",
  };
  return <Tag color={colorMap[status] || "blue"}>{status}</Tag>;
};

export default function Campaigns() {
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
  });
  const {
    refetch,
    isLoading,
    data: campaignsData,
  } = useQuery({
    queryKey: ["userBusinesses"],
    queryFn: () =>
      getAllCampaigns({
        currentPage: pagination.currentPage,
        pageSize: pagination.pageSize,
      }),
  });

  if (isLoading) return <Loading />;

  console.log("Campaigns Data:", campaignsData);

  const columns = [
    {
      title: "Buisness Name",
      key: "buisness_name",
      width: 180,
      ellipsis: true,
      render: (record) => record?.business?.name,
    },
    {
      title: "Name",
      dataIndex: "campaign_name",
      key: "campaign_name",
      width: 200,
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <span className="ellipsis-cell">{text}</span>
        </Tooltip>
      ),
    },

    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 200,
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <span className="ellipsis-cell">{text}</span>
        </Tooltip>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text) => getStatusTag(text),
    },
    {
      title: "Deliver At",
      dataIndex: "deliver_at",
      key: "deliverAt",
      width: 180,
      render: (value) => (value ? new Date(value).toLocaleString() : "-"),
    },
    {
      title: "Target Segment",
      dataIndex: "target_segment",
      key: "target_segment",
      width: 120,
      ellipsis: true,
    },

    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      render: (value) => (value ? new Date(value).toLocaleString() : "-"),
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 180,
      render: (value) => (value ? new Date(value).toLocaleString() : "-"),
    },

    {
      title: "Reward Earned",
      dataIndex: "min_rewards_earned",
      key: "min_rewards_earned",
      width: 120,
    },
    {
      title: "Notification",
      key: "oneSignalLink",
      width: 150,
      render: (record) => {
        if (record.status === "COMPLETED" && record.oneSignalLink) {
          return (
            <Button
              type="link"
              onClick={() => window.open(record.oneSignalLink, "_blank")}
            >
              View Notifications
            </Button>
          );
        }
        return "-";
      },
    },
    {
      title: "",
      key: "actions",
      render: (text, record) => {
        if (record.status === "PENDING") {
          return (
            <Space>
              <Button
                style={{ color: "green" }}
                onClick={() => {
                  Modal.confirm({
                    title: "Accept Campaign?",
                    content: "Are you sure you want to accept this campaign?",
                    okText: "Yes, Accept",
                    cancelText: "Cancel",
                    onOk: async () => {
                      await campaignAccept(record.id)
                        .then(() => {
                          message.success("Campaign accepted.");
                          refetch();
                        })
                        .catch((err) => {
                          console.error(err);
                          message.error("Failed to accept campaign");
                        });
                    },
                  });
                }}
              >
                Accept
              </Button>

              <Button
                danger
                type="primary"
                onClick={() => {
                  Modal.confirm({
                    title: "Reject Campaign?",
                    content: "Are you sure you want to reject this campaign?",
                    okText: "Yes, Reject",
                    cancelText: "Cancel",
                    onOk: () =>
                      campaignReject(record.id)
                        .then(() => {
                          refetch();
                          message.success("Campaign rejected.");
                        })
                        .catch(() =>
                          message.error("Failed to reject campaign"),
                        ),
                  });
                }}
              >
                Reject
              </Button>
            </Space>
          );
        }

        return (
          <Dropdown
            trigger={["click"]}
            menu={{
              items: [
                {
                  key: "1",
                  label: <Link to={`edit/${record.id}`}>Edit</Link>,
                },
                {
                  key: "4",
                  danger: true,
                  label: "Delete",
                  onClick: () => handleDelete(record.id),
                },
              ],
            }}
            placement="bottomRight"
          >
            <Button type="link" onClick={(e) => e.preventDefault()}>
              <Space>
                <RxDotsVertical />
              </Space>
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  const handleDelete = (id) => {
    // Show confirmation modal
    Modal.confirm({
      title: "Are you sure you want to delete this campaign?",
      content: "Once deleted, this action cannot be undone.",
      okText: "Yes, Delete",
      cancelText: "Cancel",
      onOk: () => {
        // Proceed with the delete request if confirmed
        deleteCampaign(id)
          .then(() => {
            refetch(); // Re-fetch the campaigns or refresh the list
            message.success("Campaign is deleted successfully.");
          })
          .catch(() => {
            message.error("Something went wrong !!.");
          });
      },
    });
  };

  const handlePaginationChange = (page, size) => {
    setPagination({ currentPage: page, pageSize: size });
  };
  return (
    <ContentLayout
      header={
        <>
          <h1>
            Magic Stamp <span>Campaigns</span>
          </h1>
        </>
      }
    >
      <div className="features-tab">
        <div className="form-row">
          <Link to="create" className="btn primary">
            Create
          </Link>
          <SearchBar />
        </div>
        <TableMed
          columns={columns}
          data={campaignsData?.body?.docs}
          scroll={{ x: 1200 }}
          onPaginationChange={handlePaginationChange}
        />
      </div>
    </ContentLayout>
  );
}
